/**
 * Le contenu du panneau « Filtre » de la table des leads — Organisation ›
 * Acquisition.
 *
 * Kayla, 19-08-2026 : « j'aime pas comment c'est fait, les filtres. Pourquoi
 * tu n'as pas fait comme on a dans Conversation, juste un bouton Filtre ? Ça
 * aurait été plus simple. »
 *
 * Les cinq rangées qui mangeaient un tiers de l'écran en permanence passent
 * derrière un bouton. Depuis la refonte au nouveau thème (01-11-2026), ce
 * bouton et son panneau flottant sont ceux du `Tableau` de la librairie (slot
 * `filtre`) : le panneau gère lui-même le clic dehors et Échap. Ce composant
 * n'en dessine plus que le CONTENU — mêmes réglages, même contrat : l'état vit
 * chez l'appelant, il ne fait qu'émettre le suivant.
 *
 * ⛔ Aucun filtre coché par défaut, ici comme avant : un écran qui montre un
 * sous-ensemble en ayant l'air complet coûte plus cher que celui qui montre
 * trop. Ce qui filtre se lit sur les puces de la table, panneau fermé.
 */
import type { ReactNode } from "react";

import { Button, Infobulle, Saisie } from "@/shared/ui";

import {
  CONN_VERDICT_EMOJI,
  CONN_VERDICT_LABELS,
  DAY_LABELS,
  DAY_LABELS_FULL,
  SHIFTS,
} from "../constants";
import { statusLabel } from "../format";
import { FILTRES_VIDES, type FiltresLeads } from "../leads";
import type { ConnVerdict, DayKey, SlotKey } from "../types";
import { DAY_KEYS } from "../types";

/** Les trois verdicts, du meilleur au moins bon — même ordre que le tri. */
const VERDICTS: ConnVerdict[] = ["good", "ok", "bad"];

/**
 * Les trois états d'une candidature, du plus avancé au moins avancé.
 *
 * 🩸 L'ordre est délibéré, et c'est l'inverse de celui de la base. Kayla ne
 * cherche pas « un statut », elle cherche des gens : « j'aimerais bien pouvoir
 * filtrer que ceux qui ont terminé le test ». Ce qu'elle demande est donc en
 * tête du panneau, et la section s'appelle « Test » — pas « Statut », qui ne
 * dit pas de quoi il est le statut.
 */
const STATUTS = ["submitted", "started", "created"];

interface Props {
  filtres: FiltresLeads;
  onChange: (suivant: FiltresLeads) => void;
}

export function LeadsFiltresPopover({ filtres, onChange }: Props) {
  function bascule<T>(liste: T[], valeur: T): T[] {
    return liste.includes(valeur) ? liste.filter((x) => x !== valeur) : [...liste, valeur];
  }

  return (
    // Le panneau du Tableau ne défile pas : sur un cadre bas ou un téléphone, c'est ce bloc qui défile.
    <div className="flex max-h-[min(68vh,540px)] flex-col gap-3 overflow-y-auto pr-1">
      <Section titre="Test">
        {STATUTS.map((st) => (
          <Choix
            key={st}
            coche={filtres.statuts.includes(st)}
            onClick={() => onChange({ ...filtres, statuts: bascule(filtres.statuts, st) })}
          >
            {statusLabel(st)}
          </Choix>
        ))}
      </Section>

      <Section titre="Shift">
        {SHIFTS.map((s) => (
          <Choix
            key={s.key}
            coche={filtres.shifts.includes(s.key)}
            aide={`${s.name} — ${s.letter}`}
            onClick={() =>
              onChange({ ...filtres, shifts: bascule<SlotKey>(filtres.shifts, s.key) })
            }
          >
            {s.letter}
          </Choix>
        ))}
      </Section>

      <Section titre="Jour">
        {DAY_KEYS.map((j) => (
          <Choix
            key={j}
            coche={filtres.jour === j}
            aide={DAY_LABELS_FULL[j as DayKey]}
            onClick={() =>
              onChange({ ...filtres, jour: filtres.jour === j ? null : (j as DayKey) })
            }
          >
            {DAY_LABELS[j as DayKey]}
          </Choix>
        ))}
      </Section>

      <Section titre="Âge">
        <Choix
          coche={filtres.age?.sens === "moins"}
          onClick={() =>
            onChange({
              ...filtres,
              age: filtres.age?.sens === "moins" ? null : { seuil: 25, sens: "moins" },
            })
          }
        >
          &lt; 25 ans
        </Choix>
        <Choix
          coche={filtres.age?.sens === "plus"}
          onClick={() =>
            onChange({
              ...filtres,
              age: filtres.age?.sens === "plus" ? null : { seuil: 25, sens: "plus" },
            })
          }
        >
          ≥ 25 ans
        </Choix>
      </Section>

      <Section titre="Connexion">
        {VERDICTS.map((v) => (
          <Choix
            key={v}
            coche={filtres.connexions.includes(v)}
            onClick={() =>
              onChange({ ...filtres, connexions: bascule<ConnVerdict>(filtres.connexions, v) })
            }
          >
            {CONN_VERDICT_EMOJI[v]} {CONN_VERDICT_LABELS[v]}
          </Choix>
        ))}
      </Section>

      {/* Remet TOUT à zéro — la recherche et les liens vides compris, que les
          puces de la table ne portent pas : ce bouton-là est le geste « je
          repars de la liste complète », pas « je lève un filtre ». */}
      <Button variant="secondary" size="lg" onClick={() => onChange(FILTRES_VIDES)}>
        Tout afficher
      </Button>
    </div>
  );
}

/** Une section du panneau : son titre (`SectionDeFiltres`) et ses choix, qui vont à la ligne. */
function Section({ titre, children }: { titre: string; children: ReactNode }) {
  return (
    <Saisie.SectionDeFiltres titre={titre}>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </Saisie.SectionDeFiltres>
  );
}

/**
 * Un choix de filtre : une `Puce` de la librairie (un `Button` qu'on bascule,
 * `aria-pressed`). La marque n'est que sur ce qui est CHOISI.
 * `aide` : ce que la lettre ou l'abréviation veut dire, en infobulle.
 */
function Choix({
  coche,
  onClick,
  aide,
  children,
}: {
  coche: boolean;
  onClick: () => void;
  aide?: string;
  children: ReactNode;
}) {
  const bouton = (
    <Saisie.Puce taille="lg" actif={coche} onClick={onClick}>
      {children}
    </Saisie.Puce>
  );
  return aide ? <Infobulle texte={aide}>{bouton}</Infobulle> : bouton;
}
